import { useMemo, type ReactElement } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../../lib/utils.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import {
  ChartTooltip,
  type ChartTooltipProps,
} from "../../molecules/ChartTooltip/index.js";

/** One observed reading. `t` is epoch milliseconds so the x-axis can be a real
 * TIME scale — which is what lets a target date sit on it whether or not a
 * reading happens to fall on that day, and what keeps unevenly-spaced readings
 * unevenly spaced. */
export interface MeasurementPoint {
  readonly t: number;
  readonly value: number;
}

export interface MeasurementTrendChartProps {
  /** Readings, ascending by `t`. The component does not sort — the caller owns
   * ordering, because it also owns which range it fetched. */
  readonly points: readonly MeasurementPoint[];
  /** Horizontal reference for the value being aimed at. Omit for no line. */
  readonly target?: number;
  /** Vertical reference for the date it is aimed at (epoch ms). Rendered only
   * when it falls inside the plotted window — an out-of-range marker would be
   * clamped to an edge and read as "the target is due now", which is worse than
   * not drawing it. */
  readonly targetDate?: number;
  /** Appended to values in the tooltip. */
  readonly unit?: string;
  /** Accessible name for the figure. Required: a chart with no name is an
   * unlabelled region to a screen reader. */
  readonly ariaLabel: string;
  /** Rendered instead of the plot when `points` is empty. */
  readonly emptyMessage?: string;
  /** Epoch-ms → axis/tooltip label. Defaults to the ISO calendar day, which is
   * locale-independent and therefore deterministic under test and VR. */
  readonly formatDate?: (t: number) => string;
  readonly className?: string;
}

const CHART_MARGIN = { top: 8, right: 16, bottom: 4, left: -8 } as const;

/** Locale-independent by design — see `formatDate`. */
function isoDay(t: number): string {
  return new Date(t).toISOString().slice(0, 10);
}

/** The hover readout: the reading's day, then its value with the unit.
 *  recharts clones it with `active`, `label` (epoch ms) and `payload`. */
function ReadingTooltip({
  active,
  label,
  payload,
  formatDate,
  unit,
}: Pick<ChartTooltipProps, "active" | "label" | "payload"> & {
  readonly formatDate: (t: number) => string;
  readonly unit?: string | undefined;
}): ReactElement | null {
  return (
    <ChartTooltip
      active={active}
      label={typeof label === "number" ? formatDate(label) : label}
      payload={payload}
      formatValue={(value) =>
        unit ? `${String(value)} ${unit}` : String(value)
      }
    />
  );
}

/**
 * A time-series of observed values with an optional target reference — the
 * "how has this measure actually moved, and where is it meant to get to"
 * picture.
 *
 * Data-blind like every organism here: the caller resolves its own domain into
 * `points`, a `target` and a `targetDate`, and gets no opinion back about what
 * those mean. It is deliberately NOT `RunChartWidget`, which looks similar and
 * is not reusable here: that one is a canvas widget bound to `WidgetBodyProps`
 * and `RunChartParams`, plots a categorical label axis (so a target DATE cannot
 * be placed on it), and colours its line by "above/below target" — a judgement
 * that is wrong for a lower-is-better measure and one this component leaves to
 * the surrounding page.
 *
 * The chart itself is `aria-hidden`. It carries no information the page does not
 * also state textually (the current value, the target, the range), and an SVG of
 * plotted points is noise to a screen reader; the labelled figure wrapper is
 * what gets announced.
 */
export function MeasurementTrendChart({
  points,
  target,
  targetDate,
  unit,
  ariaLabel,
  emptyMessage = "No readings in this range yet.",
  formatDate = isoDay,
  className,
}: MeasurementTrendChartProps): ReactElement {
  const reducedMotion = useReducedMotion();
  const data = useMemo(() => points.map((p) => ({ ...p })), [points]);

  // A single reading has no extent, so recharts would collapse the domain to a
  // point and draw nothing visible. Pad it by a day either side so the dot lands
  // in the middle of a real axis. The empty case never reaches the plot (the
  // early return below), so its value is inert — stated as a real pair anyway so
  // the type stays a plain `[number, number]`.
  const domain = useMemo<[number, number]>(() => {
    const first = points[0]?.t ?? 0;
    const last = points[points.length - 1]?.t ?? 1;
    if (first === last) return [first - 86_400_000, last + 86_400_000];
    return [first, last];
  }, [points]);

  const showTargetDate =
    targetDate !== undefined &&
    targetDate >= domain[0] &&
    targetDate <= domain[1];

  if (points.length === 0) {
    return (
      // Kept hand-built: the dashed 224px frame holds the chart's place with
      // 14px text; EmptyState has no size that renders it, and no look row
      // lists the change.
      <figure
        aria-label={ariaLabel}
        className={cn(
          "grid h-56 place-items-center rounded-lg border border-dashed border-pb-border px-4 text-center",
          className,
        )}
      >
        <figcaption className="text-sm text-pb-text-muted">
          {emptyMessage}
        </figcaption>
      </figure>
    );
  }

  return (
    <figure aria-label={ariaLabel} className={cn("h-56 w-full", className)}>
      <div className="h-full w-full" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--pb-border))"
              vertical={false}
            />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={domain}
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={{ stroke: "hsl(var(--pb-border))" }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip
              cursor={{ stroke: "hsl(var(--pb-border))", strokeWidth: 1 }}
              content={<ReadingTooltip formatDate={formatDate} unit={unit} />}
            />
            {target !== undefined ? (
              <ReferenceLine
                y={target}
                ifOverflow="extendDomain"
                stroke="hsl(var(--pb-amber))"
                strokeDasharray="4 4"
                label={{
                  value: `Target ${target}`,
                  position: "insideTopRight",
                  fontSize: 10,
                  fill: "hsl(var(--pb-amber))",
                }}
              />
            ) : null}
            {showTargetDate ? (
              <ReferenceLine
                x={targetDate}
                stroke="hsl(var(--pb-amber))"
                strokeDasharray="2 4"
                label={{
                  value: "Due",
                  position: "top",
                  fontSize: 10,
                  fill: "hsl(var(--pb-amber))",
                }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="value"
              name="Value"
              stroke="hsl(var(--pb-cyan))"
              strokeWidth={2.25}
              dot={{ r: 3, fill: "hsl(var(--pb-cyan))", strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              isAnimationActive={!reducedMotion}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}
